import { useState } from 'react';
import { X, Mail, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.js';
import { Modal, ModalHeader, ModalBody, ModalFooter } from './ui/modal.js';

interface PasswordResetModalProps {
  onClose: () => void;
  /** Optional pre-filled email (e.g. when admin clicks "Resend invite" from /admin/operators). */
  initialEmail?: string;
}

/**
 * Modal for triggering Firebase's password-reset email. Used both from
 * the LoginPage "Forgot password?" link AND from the AdminOperatorsPage
 * "Resend invite" action — Firebase fires the same email in both cases
 * (single template configured in Firebase Console).
 *
 * Ported from packages/vireo-sentinel/vireo-client/src/components/PasswordResetModal.tsx
 * with assayer color tokens and inline error display (no toast lib in
 * assayer-manager).
 */
export function PasswordResetModal({ onClose, initialEmail }: PasswordResetModalProps) {
  const [email, setEmail] = useState(initialEmail ?? '');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { resetPassword } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsProcessing(true);
    try {
      await resetPassword(email);
      setIsSuccess(true);
      setTimeout(onClose, 3000);
    } catch (err: any) {
      // For security, don't reveal whether the email exists.
      if (err?.code === 'auth/user-not-found') {
        setIsSuccess(true);
        setTimeout(onClose, 3000);
      } else if (err?.code === 'auth/too-many-requests') {
        setError('Too many requests. Please try again later.');
        setIsProcessing(false);
      } else {
        setError(err?.message || 'Failed to send password reset email.');
        setIsProcessing(false);
      }
    }
  };

  return (
    <Modal onClose={onClose} canClose={!isProcessing} size="md">
      <ModalHeader>
        <div className="flex items-center">
          <Mail className="h-5 w-5 text-primary-04 mr-2" />
          <h2 className="text-xl font-semibold text-grey-700">Reset Password</h2>
        </div>
        <button
          onClick={onClose}
          className="text-grey-500 hover:text-grey-700 transition-colors touch-target"
          disabled={isProcessing}
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
      </ModalHeader>

      {/* Body */}
      {isSuccess ? (
        <ModalBody>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 mb-4 bg-success-50 rounded-full">
                <CheckCircle className="h-8 w-8 text-success-600" />
              </div>
              <h3 className="text-lg font-semibold text-grey-700 mb-2">
                Email Sent
              </h3>
              <p className="text-grey-500 mb-4">
                If an account exists for that email, a password reset link is on its way.
              </p>
              <p className="text-sm text-grey-400">Closing automatically…</p>
            </div>
        </ModalBody>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <ModalBody>
            <div className="mb-6 p-4 bg-primary-50 rounded-lg">
              <div className="flex items-start">
                <AlertCircle className="h-4 w-4 text-primary-04 mt-0.5 mr-2 flex-shrink-0" />
                <div className="text-sm text-grey-700">
                  Enter your email and we'll send you a link to set a new password.
                </div>
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-danger-50 border border-danger-200 rounded-lg">
                <p className="text-danger-600 text-sm">{error}</p>
              </div>
            )}

            <div className="mb-6">
              <label htmlFor="reset-email" className="block text-sm font-medium text-grey-700 mb-2">
                Email
              </label>
              <input
                id="reset-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-04 focus:border-transparent"
                placeholder="you@example.com"
                disabled={isProcessing}
                autoFocus
              />
            </div>

          </ModalBody>
          <ModalFooter>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-grey-700 border border-grey-200 rounded-lg hover:bg-grey-50 transition-colors"
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-primary-04 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
                disabled={isProcessing || !email}
              >
                {isProcessing ? 'Sending…' : 'Send Reset Link'}
              </button>
          </ModalFooter>
        </form>
      )}
    </Modal>
  );
}
